import { useState } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Comment } from '@/types';
import { CommentsService } from '@/services/commentsService';
import { fromNow } from '@/common/utils/date';
import { useAuth } from '@/auth';


const ROLES_BADGE = {
  super_admin: { variant: 'primary', text: 'Super Admin' },
  tramitadora: { variant: 'primary', text: 'Tramitadora' },
  contador: { variant: 'outline', text: 'Contador' },
  escribano: { variant: 'outline', text: 'Escribano' },
  usuario: { variant: 'outline', text: 'Usuario' }
};



interface UseCommentsOptions {
  tramiteId?: number;
}

export const useComments = ({ tramiteId }: UseCommentsOptions = {}) => {
  const queryClient = useQueryClient();
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const { user } = useAuth();

  // Query para obtener comentarios
  const {
    data: comments = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['comments', tramiteId],
    queryFn: async () => {
      if (tramiteId) {
        return CommentsService.getComments(tramiteId);
      }
    },
    enabled: !!tramiteId,
    refetchOnWindowFocus: false,
  });

  // Mutación para crear comentario
  const addCommentMutation = useMutation({
    mutationFn: async (comment: string) => {
      return CommentsService.createComment(tramiteId, comment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', tramiteId] });
    }
  });

  // Mutación para crear respuesta
  const addReplyMutation = useMutation({
    mutationFn: async ({ parentId, replyText }: { parentId: number; replyText: string }) => {
      return CommentsService.createReply(tramiteId, parentId, replyText);
    },
    onSuccess: () => {
      setReplyingTo(null);
      queryClient.invalidateQueries({ queryKey: ['comments', tramiteId] });
    }
  });

  // Mutación para actualizar comentario
  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, comment }: { commentId: number; comment: string }) => {
      return CommentsService.updateComment(tramiteId, commentId, comment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', tramiteId] });
    }
  });

  // Mutación para eliminar comentario
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: number) => {
      return CommentsService.deleteComment(tramiteId, commentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', tramiteId] });
    }
  });

  // Funciones helper
  const getTotalComments = (commentsList: Comment[]): number => {
    return commentsList.reduce((total, comment) => {
      const countChildren = (c: Comment): number => {
        return 1 + c.children.reduce((sum, child) => sum + countChildren(child), 0);
      };
      return total + countChildren(comment);
    }, 0);
  };

  const getRoleBadge = () => {
    const roles = user.roles;
    const firstRole = roles.length > 0 ? roles[0].toLowerCase() : 'usuario';
    return ROLES_BADGE[firstRole] ?? { variant: 'outline', text: 'Usuario' };
  };

  const formatDate = (dateString: string) => {
    return fromNow(dateString);
  };

  return {
    // Data
    comments,
    isLoading,
    error,
    totalComments: getTotalComments(comments),

    // State
    replyingTo,
    setReplyingTo,

    // Mutations
    addComment: addCommentMutation.mutate,
    addReply: addReplyMutation.mutate,
    updateComment: updateCommentMutation.mutate,
    deleteComment: deleteCommentMutation.mutate,

    // Mutation states
    isAddingComment: addCommentMutation.isPending,
    isAddingReply: addReplyMutation.isPending,
    isUpdatingComment: updateCommentMutation.isPending,
    isDeletingComment: deleteCommentMutation.isPending,

    // Helper functions
    getRoleBadge,
    formatDate,
    refetch
  };
};

export default useComments;
